import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
  Button,
} from "@mui/material";
import SnackbarComp from "../../components/Snackbar/SnackbarComp";
import ElectricBoltSharpIcon from "@mui/icons-material/ElectricBoltSharp";
import OfflineBoltOutlinedIcon from "@mui/icons-material/OfflineBoltOutlined";
import colors from "../../theme";
import {
  fetchChargingPointData,
  getChargePointSwitchStatus,
  switchChargePoint,
  fetchChargingSchedule,
  createChargingSchedule,
  updateScheduleStatus,
} from "../../services/api";
import IOSSwitch from "../../components/DashboardComponents/IOSSwitch";
import {
  timePickerSlotProps,
  timePickerSx,
} from "../../styles/timePickerStyle";

import { useSelector, useDispatch } from "react-redux";
import { setNewNotificationReceived } from "../../store/notificationSlice";
import dayjs from "dayjs";
import { DemoItem } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import GuageDisplay from "../../components/DashboardComponents/GuageDisplay";
import { switchSx } from "../../styles/SwitchStyle";

const Gauge = ({ selectedChargingPoint, selectedBuilding, onToggle, offToggle }) => {
  const [clientHeight] = useState(window.innerHeight);
  const dispatch = useDispatch();
  const [gaugeValue, setGaugeValue] = useState(0);
  const [chargingStatus, setChargingStatus] = useState("Not Charging");
  const [lastUpdatedTime, setLastUpdatedTime] = useState("");
  const [isOldDate, setIsOldDate] = useState(false);
  const [switchState, setSwitchState] = useState(false);
  const [retryInterval, setRetryInterval] = useState(15);
  const [initialMount, setInitialMount] = useState(true);
  const [error, setError] = useState(null);
  const [statusCode, setStatusCode] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userInitiatedSwitch, setUserInitiatedSwitch] = useState(false);
  const [fiveSecondsFlag, setFiveSecondsFlag] = useState(false);
  const [scheduleChargingObj, setScheduleChargingObj] = useState({
    id: "",
    startTime: "",
    endTime: "",
    isActive: false,
  });
  const [originalTimes, setOriginalTimes] = useState({
    startTime: "",
    endTime: "",
  });
  const latestRequestTimestampByDeviceRef = useRef({});

  const newNotificationReceived = useSelector(
    (state) => state.notification.newNotificationReceived
  );

  const payloadCurrentPower = useSelector(
    (state) => state.payloadCurrentPower.payloadCurrentPower
  );

  useEffect(() => {
  const handleSwitchChange = async () => {
    onToggle(switchState);
  };

  handleSwitchChange();
}, [switchState, onToggle]);


  useEffect(() => {
    const intervalId = setInterval(() => {
      setRetryInterval((prevRetryInterval) => {
        const newRetryInterval = prevRetryInterval - 1;
        return newRetryInterval;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (switchState && userInitiatedSwitch) {
      const newIsActive = false;
      setScheduleChargingObj((prevState) => ({
        ...prevState,
        isActive: newIsActive,
      }));
      setUserInitiatedSwitch(false); // Reset the flag after handling the switch
    }
  }, [switchState, userInitiatedSwitch]);

  const fetchData = async () => {
    try {
      if (selectedChargingPoint) {
        const data = await fetchChargingPointData(selectedChargingPoint);
        const dataPoint = data?.data[0];
        let currentPowerInWatts = data?.data[0]?.currentPower || 0;

        if (dataPoint) {
          const timeDifference =
            new Date().getTime() - new Date(dataPoint.time).getTime();
          if (currentPowerInWatts === 0) {
            setChargingStatus("Not Charging");
          } else {
            setChargingStatus("Charging");
          }

          if (timeDifference > 300000) {
            setIsOldDate(true);
          } else {
            setIsOldDate(false);
          }

          setGaugeValue(parseFloat((currentPowerInWatts / 1000).toFixed(3)));
          const formattedDate = dayjs(dataPoint.time).format(
            "DD/MM/YYYY, HH:mm:ss"
          );
          setLastUpdatedTime(formattedDate);
        } else {
          setLastUpdatedTime("No data available");
          setIsOldDate(true);
          setChargingStatus("Not Charging");
          setGaugeValue(0);
        }
      }
    } catch (error) {
      setLastUpdatedTime("Error fetching data");
      setIsOldDate(true);
      setChargingStatus("Not Charging");
      setGaugeValue(0);
    }
  };

  const fetchSwitchStatus = async () => {
    if (!selectedChargingPoint) {
      return;
    }
    try {
      const result = await getChargePointSwitchStatus(selectedChargingPoint);
      if (Number(result.requestTimestamp) < (latestRequestTimestampByDeviceRef.current[selectedChargingPoint] || 0)) {
        console.log("Ignored: ",Number(result.requestTimestamp), result.requestTimestamp);
        // Return silently if API response is from an older request
        return;
      }
      latestRequestTimestampByDeviceRef.current[selectedChargingPoint] = Number(result.requestTimestamp);
      if (result.status === "0401") {
        setStatusCode(400);
        setError(result.message);
        setOpenSnackbar(true);
      }
      setSwitchState(result.switchStatus);
    } catch (error) {
      if (Number(error.requestTimestamp) < (latestRequestTimestampByDeviceRef.current[selectedChargingPoint] || 0)) {
        // Return silently if API response is from an older request
        return;
      }
      latestRequestTimestampByDeviceRef.current[selectedChargingPoint] = Number(error.requestTimestamp);
      console.error("Error fetching switch status:", error);
    }
  };

  const fetchChargingScheduleData = useCallback(async () => {
    try {
      if (selectedChargingPoint) {
        const scheduleData = await fetchChargingSchedule(selectedChargingPoint);
        if (scheduleData) {
          const startTime = dayjs(scheduleData.startTime, "HH:mm");
          const endTime = dayjs(scheduleData.endTime, "HH:mm");

          setScheduleChargingObj({
            id: scheduleData.id,
            startTime,
            endTime,
            isActive: scheduleData.isActive,
          });

          setOriginalTimes({
            startTime,
            endTime,
          });
        } else {
          const startTime = dayjs();
          const endTime = dayjs().add(1, "hour");
          setScheduleChargingObj({
            id: "",
            startTime,
            endTime,
            isActive: false,
          });
          setOriginalTimes({
            startTime,
            endTime,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching charging schedule:", error);
    }
  }, [selectedChargingPoint]);

  useEffect(() => {
    fetchChargingScheduleData();
  }, [selectedChargingPoint, switchState, fetchChargingScheduleData]);

  useEffect(() => {
    if (retryInterval === 0 || initialMount) {
      setInitialMount(false);
      if (
        selectedChargingPoint === "9E0E85720-0" ||
        selectedChargingPoint === "9E0EAB4BC-0" ||
        selectedChargingPoint === "9E0EAB4BC-1" ||
        selectedChargingPoint === "E6E3F5DF8-0"
      ) {
        setRetryInterval(3);
      } else {
        setRetryInterval(10);
      }
      if (fiveSecondsFlag) {
        fetchSwitchStatus();
        setFiveSecondsFlag(false);
      } else {
        fetchSwitchStatus();
        fetchData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryInterval]);

  useEffect(() => {
    if (
      selectedChargingPoint === "9E0E85720-0" ||
      selectedChargingPoint === "9E0EAB4BC-0" ||
      selectedChargingPoint === "9E0EAB4BC-1" ||
      selectedChargingPoint === "E6E3F5DF8-0"
    ) {
      setRetryInterval(3);
    } else {
      setRetryInterval(10);
    }
    fetchData();
    fetchSwitchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChargingPoint]);

  useEffect(() => {
    if (newNotificationReceived) {
      setGaugeValue((payloadCurrentPower / 1000).toFixed(2));
      if (payloadCurrentPower === 0) {
        setChargingStatus("Not Charging");
      } else {
        setChargingStatus("Charging");
      }
      fetchSwitchStatus();
      dispatch(setNewNotificationReceived(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newNotificationReceived, selectedChargingPoint]);

  const handleToggle = async (event) => {
    const action = event.target.checked ? "On" : "Off";
    setUserInitiatedSwitch(true); // Set the flag when user toggles
    let message = null;
    let statusCode = null;
    let resp = null;
    let applyResponse = true;
    try {
      setSwitchState(!switchState);
      resp = await switchChargePoint(
        selectedChargingPoint,
        selectedBuilding,
        action
      );
      if (Number(resp.data.requestTimestamp) < (latestRequestTimestampByDeviceRef.current[selectedChargingPoint] || 0)) {
        console.log("Ignored: ", new Date(resp.data.requestTimestamp).toISOString(), resp.data.requestTimestamp);
        // Do not apply response if API response is from an older request
        applyResponse = false;
      } else {
        latestRequestTimestampByDeviceRef.current[selectedChargingPoint] = Number(resp.data.requestTimestamp);
        message = resp.message;
        statusCode = resp.status;
      }
    } catch (error) {
      if (Number(error.requestTimestamp) < (latestRequestTimestampByDeviceRef.current[selectedChargingPoint] || 0)) {
        // Do not apply response if API response is from an older request
        applyResponse = false;
      } else {
        latestRequestTimestampByDeviceRef.current[selectedChargingPoint] = Number(error.requestTimestamp);
        if (error.response?.data?.status === "0402") {
          await fetchChargingScheduleData(); // Update charging schedule if limit exceeds.
        }
        message = action === "On" && /subscription/i.test(error.response?.data?.message || "")
          ? "Cannot start charging without a subscription"
          : `Could not turned device ${action}. ${error.response?.data?.message || ""}`;
        statusCode = error.response?.data?.status;

        setSwitchState(switchState);
      }
    } finally {
      if (applyResponse) {
        setStatusCode(statusCode);
        setError(`${message}`);
        setOpenSnackbar(true);
        setRetryInterval(5);
        setFiveSecondsFlag(true);
      }
    }
  };
  const handleToggleScheduleCharging = async () => {
    const previousIsActive = scheduleChargingObj.isActive;
    const newIsActive = !previousIsActive;
    setScheduleChargingObj((prevState) => ({
      ...prevState,
      isActive: newIsActive,
    }));
    await updateScheduleStatusHandler(newIsActive, previousIsActive);
  };

  const updateScheduleStatusHandler = async (newIsActive, previousIsActive) => {
    try {
      if (scheduleChargingObj.id) {
        await updateScheduleStatus(newIsActive, scheduleChargingObj.id);
      }
    } catch (error) {
      console.error("Error updating schedule status:", error);
      setScheduleChargingObj((prevState) => ({
        ...prevState,
        isActive: previousIsActive,
      }));
      setError(error?.message);
      setStatusCode(error?.status);
      setOpenSnackbar(true);
    }
  };

  const handleStartTimeChange = (newStartTime) => {
    setScheduleChargingObj((prevState) => {
      if (newStartTime.isSame(prevState.endTime)) {
        setError(
          "Start time and end time cannot be the same. Please select a different time."
        );
        setStatusCode(400);
        setOpenSnackbar(true);
        return prevState;
      }

      return {
        ...prevState,
        startTime: newStartTime,
      };
    });
  };

  const handleEndTimeChange = (newEndTime) => {
    setScheduleChargingObj((prevState) => {
      if (newEndTime.isSame(prevState.startTime)) {
        setError(
          "Start time and end time cannot be the same. Please select a different time."
        );
        setStatusCode(400);
        setOpenSnackbar(true);
        return prevState;
      }

      return {
        ...prevState,
        endTime: newEndTime,
      };
    });
  };

  const handleChargingScheduleUpdate = async () => {
    setIsLoading(true);
    try {
      if (
        !scheduleChargingObj.startTime.isSame(originalTimes.startTime) ||
        !scheduleChargingObj.endTime.isSame(originalTimes.endTime)
      ) {
        const createdSchedule = await createChargingSchedule(
          scheduleChargingObj.startTime.format("YYYY-MM-DDTHH:mm:ss"),
          scheduleChargingObj.endTime.format("YYYY-MM-DDTHH:mm:ss"),
          selectedChargingPoint
        );

        if (createdSchedule.status === "200") {
          setError(createdSchedule.message);
          setStatusCode(createdSchedule.status);
          setOpenSnackbar(true);

          setOriginalTimes({
            startTime: scheduleChargingObj.startTime,
            endTime: scheduleChargingObj.endTime,
          });

          // Update scheduleChargingObj with the new id
          setScheduleChargingObj((prevState) => ({
            ...prevState,
            id: createdSchedule.data.scheduleId,
          }));
        }
      }
    } catch (error) {
      console.error("Error updating/creating charging schedule:", error);
      setError(error?.message);
      setStatusCode(error?.status);
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 1,
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            minWidth: { md: "30vw", lg: "20vw" },
            minHeight: { md: "30vh", lg: "20vh" },
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            {chargingStatus === "Not Charging" ? (
              <OfflineBoltOutlinedIcon
                sx={{ color: "gray", fontSize: "40px !important" }}
              />
            ) : (
              <ElectricBoltSharpIcon
                sx={{ color: colors.primary, fontSize: "40px !important" }}
              />
            )}
          </Box>
          <GuageDisplay gaugeValue={gaugeValue} />
          <Box
            sx={{
              mt: -1,
              textAlign: "center",
              fontSize: "14px",
              color:
                isOldDate || lastUpdatedTime === "No data available"
                  ? "red"
                  : "black",
            }}
          >
            Last Updated: {lastUpdatedTime}
          </Box>

          <Divider
            sx={{
              color: "black",
              mx: 5,
              my: 1,
              bgcolor: "black",
            }}
          />
          <Box
            sx={{
              textAlign: "center",
              mt: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography>Off</Typography>
            <FormControlLabel
              control={
                <IOSSwitch
                  sx={{ ml: 3.5 }}
                  checked={switchState}
                  onChange={handleToggle}
                />
              }
            />
            <Typography>On</Typography>
          </Box>
          <Divider
            sx={{
              color: "black",
              mx: 5,
              mt: 1,
              bgcolor: "black",
            }}
          />
          <Box
            sx={{
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              my: 0,
            }}
          >
            <Typography>Scheduled Charging</Typography>
            <FormControlLabel
              disabled={switchState}
              control={
                <Switch
                  sx={switchSx}
                  checked={scheduleChargingObj.isActive}
                  onChange={handleToggleScheduleCharging}
                />
              }
            />
          </Box>

          <Box
            sx={{
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: 0.5,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  gap: "0.5rem",
                }}
              >
                <DemoItem>
                  <MobileTimePicker
                    disabled={switchState || !scheduleChargingObj.isActive}
                    label="On Time"
                    value={scheduleChargingObj.startTime}
                    onAccept={handleStartTimeChange}
                    sx={{...timePickerSx, width: clientHeight < 500? "7rem": "8rem",
                      ...(scheduleChargingObj.isActive && originalTimes.startTime && !scheduleChargingObj.startTime.isSame(originalTimes.startTime) && {
                          "& .MuiOutlinedInput-root": { backgroundColor: "#ffcccc" },
                        }),
                    }}
                    slotProps={timePickerSlotProps}
                  />
                </DemoItem>
                <DemoItem>
                  <MobileTimePicker
                    disabled={switchState || !scheduleChargingObj.isActive}
                    label="Off Time"
                    value={scheduleChargingObj.endTime}
                    onAccept={handleEndTimeChange}
                    sx={{...timePickerSx, width: clientHeight < 500? "7rem": "8rem",
                      ...(scheduleChargingObj.isActive && originalTimes.endTime && !scheduleChargingObj.endTime.isSame(originalTimes.endTime) && {
                          "& .MuiOutlinedInput-root": { backgroundColor: "#ffcccc" },
                        }),
                    }}
                    slotProps={timePickerSlotProps}
                  />
                </DemoItem>

                <Button
                  sx={{
                    backgroundColor: colors.secondary,
                    color: isLoading ? "white" : colors.text,
                    fontWeight: "bold",
                    padding: "7px 10px",
                    "&:hover": {
                      backgroundColor: "#1e4e4f",
                    },
                  }}
                  onClick={() => handleChargingScheduleUpdate()}
                  disabled={isLoading || !scheduleChargingObj.isActive || !originalTimes.startTime || (scheduleChargingObj.startTime.isSame(originalTimes.startTime) && scheduleChargingObj.endTime.isSame(originalTimes.endTime))}
                >
                  <Box
                    sx={{
                      width: "fit-content",
                      color: (isLoading || !scheduleChargingObj.isActive || !originalTimes.startTime || (scheduleChargingObj.startTime.isSame(originalTimes.startTime) && scheduleChargingObj.endTime.isSame(originalTimes.endTime))) ? "gray" : colors.text,
                    }}
                  >
                    {isLoading ? <CircularProgress size={14} /> : "Set"}
                  </Box>
                </Button>
              </Box>
            </LocalizationProvider>
          </Box>

          <Divider
            sx={{
              color: "black",
              mx: 5,
              mt: 2,
              mb: 0,
              bgcolor: "black",
            }}
          />
        </Box>
      </Box>
      <SnackbarComp
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        errorMessage={error}
        statusCode={statusCode}
        autoHideDuration = {30000}
      />
    </>
  );
};

export default Gauge;
